import { ONLINE_WITHIN_MINUTES } from '@/ts';

/**
 * Check-in status helpers for the Location Zone Player card view.
 *
 * Single threshold (mirrors the backend's
 * `LocationZonePlayerData::ONLINE_WITHIN_MINUTES`): a player is online (green)
 * while its last check-in is within {@link ONLINE_WITHIN_MINUTES} minutes, and
 * offline (red) beyond that window or when it has never checked in. The former
 * two-tier 4m/8m green/yellow/red model has been retired.
 */

/** Online window in seconds, derived from the shared backend constant. */
export const ONLINE_WITHIN_SECONDS = ONLINE_WITHIN_MINUTES * 60;

export type CheckinStatusColor = 'green' | 'red';

/** Whole seconds elapsed between a timestamp and nowMs; null when missing/unparseable. */
export function secondsSince(date: string | Date | null | undefined, nowMs: number): number | null {
    if (!date) {
        return null;
    }
    const timestampMs = new Date(date).getTime();
    if (Number.isNaN(timestampMs)) {
        return null;
    }
    return Math.max(0, Math.floor((nowMs - timestampMs) / 1000));
}

export function checkinStatusColor(seconds: number | null): CheckinStatusColor {
    if (seconds === null || seconds >= ONLINE_WITHIN_SECONDS) {
        return 'red';
    }
    return 'green';
}

/** Compact human duration: "42s", "5m 12s", "3h 24m", "6d". */
export function formatCompactDuration(seconds: number): string {
    if (seconds < 60) {
        return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        const remainderSeconds = seconds % 60;
        return remainderSeconds ? `${minutes}m ${remainderSeconds}s` : `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        const remainderMinutes = minutes % 60;
        return remainderMinutes ? `${hours}h ${remainderMinutes}m` : `${hours}h`;
    }
    return `${Math.floor(hours / 24)}d`;
}
